// ⚠️  COPIA de goautos-admin/src/lib/observability.ts — mantener en sync.
//     Diferencias deliberadas: APP_NAME, env vars NEXT_PUBLIC_*, y el gate de
//     consentimiento (esta app muestra banner de cookies).
'use client';

/**
 * Observabilidad: reporte de errores a PostHog (Error Tracking).
 *
 * Módulo autocontenido y COPIABLE a las otras apps del sistema (Goautos-ventas,
 * website-gocar, goauto-docs, landing-goauto). Lo único que cambia entre copias
 * es `APP_NAME` (y en Next, el prefijo de las env vars). Todas reportan al MISMO
 * proyecto de PostHog y se distinguen por la propiedad `app`.
 *
 * Qué captura:
 *  - Excepciones no manejadas y promesas rechazadas → las toma el autocapture de
 *    PostHog (`capture_exceptions`), que arma un `$exception_list` con stack real.
 *    Por eso NO las reportamos a mano: quedarían duplicadas y con peor stack.
 *  - Crashes de render → <ErrorBoundary> llama `captureException`.
 *  - Fallas de API/red (respuestas no-2xx de Supabase) → `captureApiFailure`, como
 *    EVENTO y no como excepción: no son throws y ensuciarían los Issues.
 *
 * PII: estas apps mueven datos reales de clientes (nombre, email, teléfono, RUT,
 * datos de financiamiento). `before_send` redacta propiedades sensibles y limpia
 * los query strings de las URLs ANTES de que el evento salga del browser.
 */
import posthogJs from 'posthog-js';
import type { CaptureLogOptions, CaptureResult } from 'posthog-js';

/** Nombre de esta app en PostHog. Es LO ÚNICO que cambia al copiar el módulo. */
const APP_NAME = 'website-gocar';

const TOKEN = process.env.NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN || '';
const HOST = process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com';

// En dev capturamos igual (tagueado `environment: development`) para poder verificar
// el cableado; se apaga con NEXT_PUBLIC_POSTHOG_ENABLE_DEV=false.
const IS_DEV = process.env.NODE_ENV !== 'production';
const ENABLE_IN_DEV = process.env.NEXT_PUBLIC_POSTHOG_ENABLE_DEV !== 'false';
const ENVIRONMENT = IS_DEV ? 'development' : 'production';
const ENABLED = Boolean(TOKEN) && (!IS_DEV || ENABLE_IN_DEV);

// ---------------------------------------------------------------------------
// Consentimiento
// ---------------------------------------------------------------------------

/** Misma key que usa TrackingAndConsent para GTM/GA4/Meta Pixel. */
const CONSENT_STORAGE_KEY = 'goauto_cookie_consent_v1';

function hasConsent(): boolean {
  try {
    return window.localStorage.getItem(CONSENT_STORAGE_KEY) === 'accepted';
  } catch {
    return false;
  }
}

/**
 * Persistencia según consentimiento: hasta que el visitante acepta, PostHog no
 * escribe NADA en el dispositivo (todo vive en memoria y muere con la pestaña).
 * Igual capturamos errores desde el primer pageview —que es justo donde pasan
 * los crashes— sin poner cookies por detrás del banner que le mostramos.
 */
function persistenceMode(): 'memory' | 'localStorage+cookie' {
  return hasConsent() ? 'localStorage+cookie' : 'memory';
}

/**
 * Llamar cuando el visitante ACEPTA en el banner: sube la persistencia sin
 * reinicializar el SDK.
 */
export function grantObservabilityConsent(): void {
  if (!initialized) return;
  try {
    posthogJs.set_config({ persistence: 'localStorage+cookie' });
  } catch {
    // no romper el flujo del banner
  }
}

/** Llamar cuando el visitante RECHAZA: vuelve a memoria y limpia lo guardado. */
export function revokeObservabilityConsent(): void {
  if (!initialized) return;
  try {
    posthogJs.set_config({ persistence: 'memory' });
  } catch {
    // idem
  }
}

// ---------------------------------------------------------------------------
// Filtro de ruido
// ---------------------------------------------------------------------------

/**
 * Errores conocidos NO accionables: ruido de browser/observers y requests
 * abortados. Es la MISMA lista que usa el catch global para decidir si molestar
 * al usuario con un toast, así lo que se ignora en UI se ignora en PostHog.
 *
 * No filtra errores de red reales ("Failed to fetch"): esos sí interesan.
 */
export const RESIZE_OBSERVER_NOISE = /ResizeObserver loop (completed|limit)/i;

export function isIgnorable(err: unknown): boolean {
  if (err == null) return true;
  const e = err as { name?: string; message?: string };
  if (e?.name === 'AbortError') return true;
  const msg = String(e?.message ?? err ?? '');
  if (!msg) return true;
  if (
    RESIZE_OBSERVER_NOISE.test(msg) ||
    /Non-Error promise rejection captured/i.test(msg)
  )
    return true;
  return false;
}

// ---------------------------------------------------------------------------
// Scrubbing de PII
// ---------------------------------------------------------------------------

/** Claves cuyo VALOR nunca debe salir del browser. */
const SENSITIVE_KEY =
  /email|correo|\brut\b|\bdni\b|phone|tel[eé]fono|celular|password|contrase|token|api[_-]?key|secret|authorization|first_?name|last_?name|full_?name|nombre|apellido|address|direcci[oó]n|patente|plate|\bcard\b|cvv|iban|cuenta/i;

/** Propiedades de URL: se les corta el query string y el hash. */
const URL_PROPS = [
  '$current_url',
  '$referrer',
  '$pathname',
  '$initial_current_url',
  '$initial_referrer',
  '$initial_pathname',
];

const MAX_STRING = 500;
const MAX_EXCEPTION_VALUE = 300;
const MAX_DEPTH = 4;

const REDACTED = '[redacted]';

/**
 * Redacción por FORMA del valor, no por nombre de la clave.
 *
 * Es la capa que más importa: la PII no llega en propiedades que uno controla,
 * llega DENTRO del mensaje de error. Postgres cita el valor que falló:
 *   duplicate key value violates unique constraint "customers_email_key"
 *   Key (email)=(juan.perez@gmail.com) already exists.
 * Sin esto, ese email viaja a PostHog en `$exception_list[].value`.
 */
const VALUE_PATTERNS: Array<[RegExp, string]> = [
  // JWT (tokens de Supabase en mensajes de auth) — primero, es el más largo.
  [/eyJ[A-Za-z0-9_-]{10,}(?:\.[A-Za-z0-9_-]{10,}){2}/g, '[jwt]'],
  [/Bearer\s+\S+/gi, 'Bearer [token]'],
  // `Key (email)=(juan@x.cl) already exists` → no dejamos ni la columna ni el valor.
  [/Key \([^)]*\)=\([^)]*\)/g, 'Key ([col])=([redacted])'],
  [/[\w.+-]+@[\w-]+\.[\w.]{2,}/g, '[email]'],
  // RUT chileno: 12.345.678-9 / 12345678-K
  [/\b\d{1,2}\.?\d{3}\.?\d{3}-[\dkK]\b/g, '[rut]'],
  // Celular chileno: +56 9 1234 5678
  [/(?:\+?56)?\s?9\s?\d{4}\s?\d{4}\b/g, '[phone]'],
  // Patente chilena: BBBB12 / BB BB 12
  [/\b[A-Z]{2}\s?[A-Z]{2}\s?\d{2}\b/g, '[plate]'],
];

/** Redacta PII embebida en un string libre (mensajes de error, sobre todo). */
function redactValue(text: string): string {
  let out = text;
  for (const [pattern, replacement] of VALUE_PATTERNS) {
    out = out.replace(pattern, replacement);
  }
  return out;
}

/** Deja solo origin + path: los ids y filtros viajan en el query string. */
function stripUrl(value: unknown): unknown {
  if (typeof value !== 'string') return value;
  const cut = value.search(/[?#]/);
  return cut === -1 ? value : value.slice(0, cut);
}

function scrub(value: unknown, depth = 0): unknown {
  if (value == null || depth > MAX_DEPTH) return value;

  if (typeof value === 'string') {
    const safe = redactValue(value);
    return safe.length > MAX_STRING ? `${safe.slice(0, MAX_STRING)}…` : safe;
  }

  if (Array.isArray(value)) {
    return value.map((item) => scrub(item, depth + 1));
  }

  if (typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
      out[key] = SENSITIVE_KEY.test(key) ? REDACTED : scrub(val, depth + 1);
    }
    return out;
  }

  return value;
}

/**
 * Limpia el mensaje de la excepción, que es por lejos el vector de PII más
 * probable (ver VALUE_PATTERNS). NO se toca `type` ni los nombres de función de
 * los frames: esos alimentan el fingerprint con el que PostHog agrupa los
 * Issues, y mangearlos fragmentaría la agrupación.
 */
function cleanExceptionList(props: Record<string, unknown>): void {
  const list = props.$exception_list;
  if (!Array.isArray(list)) return;

  for (const item of list) {
    const entry = item as {
      value?: unknown;
      stacktrace?: { frames?: Array<Record<string, unknown>> };
    };

    if (typeof entry?.value === 'string') {
      const safe = redactValue(entry.value);
      entry.value =
        safe.length > MAX_EXCEPTION_VALUE ? `${safe.slice(0, MAX_EXCEPTION_VALUE)}…` : safe;
    }

    for (const frame of entry?.stacktrace?.frames ?? []) {
      // Snapshot de variables locales: nunca sale del browser.
      delete frame.vars;
      if (typeof frame.context_line === 'string') {
        frame.context_line = redactValue(frame.context_line);
      }
      // Los filenames pueden traer query strings con tokens.
      if (typeof frame.filename === 'string') {
        frame.filename = stripUrl(frame.filename) as string;
      }
    }
  }
}

/**
 * Redacta los `console.*` que se van al producto Logs.
 *
 * Hace falta porque `before_send` solo ve EVENTOS: sin esto, Logs sería un
 * bypass limpio del scrubber. Y no es teórico — en producción se loguean
 * objetos de error de Supabase enteros (`Error fetching reservation:` + el
 * payload), que es exactamente donde viaja la PII.
 *
 * Reusa `redactValue` y `scrub`, los mismos que limpian las excepciones, para
 * que lo que se tapa de un lado se tape del otro.
 */
function scrubLogRecord(record: CaptureLogOptions): CaptureLogOptions | null {
  const body = redactValue(record.body ?? '');
  return {
    ...record,
    body: body.length > MAX_STRING ? `${body.slice(0, MAX_STRING)}…` : body,
    attributes: record.attributes
      ? (scrub(record.attributes) as CaptureLogOptions['attributes'])
      : undefined,
  };
}

function beforeSend(result: CaptureResult | null): CaptureResult | null {
  if (!result) return result;

  if (result.event === '$exception') {
    const props = (result.properties ?? {}) as Record<string, unknown>;
    const list = props.$exception_list as
      | Array<{ type?: string; value?: string }>
      | undefined;

    // Antes esto miraba SOLO `$exception_list[0]`. Si la lista venía vacía o con
    // otra forma (p.ej. el SDK arma la excepción desde un string suelto de
    // window.onerror, o manda las props planas viejas), `first` quedaba
    // undefined y NO se filtraba nada. Ahora se revisan todas las entradas —una
    // excepción encadenada trae el `cause` en la segunda— y hay fallback a
    // `$exception_message`/`$exception_type`.
    const probes =
      Array.isArray(list) && list.length > 0
        ? list.map((entry) => ({
            name: entry?.type,
            message: entry?.value ?? '',
          }))
        : [
            {
              name: props.$exception_type as string | undefined,
              message: (props.$exception_message as string | undefined) ?? '',
            },
          ];

    // Lo que ya decidimos no mostrarle al usuario tampoco llega a PostHog.
    // El caso de una sola entrada se evalúa tal cual como antes (incluido
    // "mensaje vacío ⇒ ignorable"); con varias, basta que una traiga ruido.
    if (probes.length === 1 && isIgnorable(probes[0])) return null;
    if (probes.some((p) => p.message && isIgnorable(p))) return null;

    const messages = probes.map((p) => p.message).join(' \n');

    // Chunk viejo tras un deploy: `chunkReload` ya recarga solo, o sea que se
    // arregla solo por diseño. Reportarlo daría un pico de
    // "Failed to fetch dynamically imported module" en CADA deploy — la razón
    // clásica por la que un equipo termina ignorando su error tracking.
    if (
      /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module/i.test(
        messages
      )
    )
      return null;

    // Errores inyectados por extensiones del browser: no son nuestros.
    if (/chrome-extension:|moz-extension:|Extension context invalidated/i.test(messages))
      return null;
  }

  const props = result.properties;
  if (props) {
    for (const key of URL_PROPS) {
      if (key in props) props[key] = stripUrl(props[key]);
    }
    for (const [key, val] of Object.entries(props)) {
      // `$exception_list` se trata aparte: su estructura importa para agrupar.
      if (key === '$exception_list') continue;
      props[key] = SENSITIVE_KEY.test(key) ? REDACTED : scrub(val);
    }
    cleanExceptionList(props);
  }

  return result;
}

// ---------------------------------------------------------------------------
// API pública
// ---------------------------------------------------------------------------

let initialized = false;

/**
 * Corta el ruido de ResizeObserver ANTES de que PostHog lo vea.
 *
 * "ResizeObserver loop completed with undelivered notifications" no es un error
 * de la app: lo dispara el browser cuando un callback de resize provoca otro
 * layout pass. Lo generan carruseles, charts, popovers de Radix y cualquier
 * iframe que se redimensione solo. Nada que arreglar, pero llegaba igual a
 * Error Tracking.
 *
 * Ya estaba filtrado en `before_send`; esto es la segunda capa, en el origen.
 * El detalle que la hace funcionar: posthog-js NO escucha con addEventListener,
 * ENVUELVE `window.onerror`. Ese slot entra en la lista de listeners recién
 * cuando alguien lo asigna —o sea, dentro de `posthogJs.init()`—, así que
 * registrando esto ANTES del init quedamos primeros y
 * `stopImmediatePropagation()` impide que el wrapper de PostHog corra.
 *
 * A propósito NO se llama `preventDefault()`: el error se sigue viendo en la
 * consola del browser (útil en dev), solo no se reporta.
 */
function suppressResizeObserverNoise(): void {
  if (typeof window === 'undefined') return;
  window.addEventListener(
    'error',
    (event: ErrorEvent) => {
      if (RESIZE_OBSERVER_NOISE.test(event?.message ?? '')) {
        event.stopImmediatePropagation();
      }
    },
    true
  );
}

/**
 * Arranca PostHog. Idempotente: la llama `main.tsx` al inicio, y por las dudas
 * cada `capture*` de acá (los imports de módulos corren antes que main.tsx, así
 * que un capture temprano igual encuentra el SDK listo).
 */
export function initObservability(): void {
  if (initialized || !ENABLED || typeof window === 'undefined') return;
  initialized = true;

  // ORDEN IMPORTANTE: antes del init, si no PostHog ya envolvió window.onerror
  // y su handler corre primero (ver el comentario de la función).
  suppressResizeObserverNoise();

  posthogJs.init(TOKEN, {
    api_host: HOST,
    autocapture: true,
    // No es una precaución, es un arreglo: con `autocapture` a secas PostHog
    // manda `$el_text` (el textContent del elemento clickeado) en cada click.
    // En /clientes, /leads y /financiamiento eso son nombres de clientes —y a
    // veces RUT o teléfono— saliendo de la app desde siempre.
    mask_all_text: true,
    mask_all_element_attributes: true,
    // Esto es lo que enciende Error Tracking: PostHog instala sus propios
    // handlers de window.onerror / unhandledrejection y arma el $exception_list
    // con stack frames (lo que permite agrupar en Issues y simbolicar).
    capture_exceptions: {
      capture_unhandled_errors: true,
      capture_unhandled_rejections: true,
      // La app usa console.error a discreción (notifyError, ErrorBoundary, etc.);
      // capturarlos inundaría los Issues con cosas ya manejadas.
      capture_console_errors: false,
    },
    // Sin cookies ni localStorage hasta que el visitante acepte el banner.
    persistence: persistenceMode(),
    // Los console.* van al producto Logs —estructurados y consultables— y NO a
    // Error Tracking. No contradice `capture_console_errors: false` de arriba:
    // un Issue es algo que hay que arreglar, un log es el contexto de alrededor.
    logs: {
      captureConsoleLogs: true,
      // Separa las apps dentro de Logs igual que `app` las separa en eventos.
      serviceName: APP_NAME,
      beforeSend: scrubLogRecord,
    },
    // Session replay explícitamente fuera de alcance.
    disable_session_recording: true,
    // Sin perfiles para visitantes anónimos: menos PII y menos consumo de cuota.
    person_profiles: 'identified_only',
    // Capa declarativa de PII: enmascara estos params en $current_url/$referrer
    // aunque el scrubber de before_send falle o alguien lo edite mal.
    mask_personal_data_properties: true,
    custom_personal_data_properties: [
      'email', 'correo', 'rut', 'dni', 'telefono', 'phone', 'celular',
      'nombre', 'name', 'apellido', 'lastname', 'direccion', 'address',
      'patente', 'plate', 'vin', 'token', 'access_token', 'refresh_token',
    ],
    before_send: beforeSend,
  });

  posthogJs.register({ app: APP_NAME, environment: ENVIRONMENT });
}

function ready(): boolean {
  if (!ENABLED) return false;
  if (!initialized) initObservability();
  return initialized;
}

/** Normaliza a Error: los errores de Supabase/PostgREST son objetos planos. */
function toError(value: unknown): Error {
  if (value instanceof Error) return value;
  const v = value as { message?: unknown; name?: unknown; code?: unknown } | null;
  const message = typeof v?.message === 'string' ? v.message : String(value);
  const err = new Error(message);
  if (typeof v?.name === 'string') err.name = v.name;
  else if (v?.code != null) err.name = `SupabaseError(${String(v.code)})`;
  return err;
}

/**
 * Reporta una excepción manejada (ErrorBoundary, catches explícitos).
 * Los errores NO manejados los toma el autocapture: no los pases por acá.
 */
export function captureException(
  error: unknown,
  context?: Record<string, unknown>
): void {
  if (!ready() || isIgnorable(error)) return;
  try {
    posthogJs.captureException(toError(error), { app: APP_NAME, ...context });
  } catch {
    // El reporte de errores nunca puede romper la app.
  }
}

export interface ApiFailure {
  /** De dónde salió: 'react-query-mutation' | 'react-query-query' | 'manual' | ... */
  source: string;
  /** Código de Postgres/PostgREST si lo hay (42501, 23505, PGRST301…). */
  code?: string;
  /** Status HTTP si lo hay. */
  status?: number;
  /**
   * Categoría YA mapeada del error (el mensaje en español de `resolveErrorMessage`).
   * Nunca el mensaje crudo: ese puede citar valores del registro.
   */
  messageClass: string;
  /** Nombre de la edge function involucrada, si aplica. */
  fnName?: string;
}

/**
 * Un error de API que el usuario efectivamente VIO (toast) o que hizo fallar una
 * pantalla. Va como evento y no como excepción: un 409 de PostgREST no es un
 * crash, y mandarlo a Error Tracking ahogaría los Issues reales.
 *
 * Complementa —no duplica— `api_request_failed`: aquel mide el transporte (toda
 * request que falló), este mide el impacto en el usuario.
 */
export function captureApiFailure(info: ApiFailure): void {
  if (!ready()) return;
  try {
    posthogJs.capture('app_error_shown', {
      app: APP_NAME,
      source: info.source,
      code: info.code,
      status: info.status,
      message_class: info.messageClass,
      fn_name: info.fnName,
    });
  } catch {
    // idem
  }
}

/**
 * Describe una request sin filtrar datos: recurso y NOMBRES de los filtros,
 * nunca sus valores.
 *
 * `/rest/v1/customers?select=*&rut=eq.12.345.678-9`
 *   → { api: 'rest', resource: 'customers', filter_keys: ['rut', 'select'] }
 */
function describeRequest(raw: string): Record<string, unknown> {
  try {
    const url = new URL(raw, typeof window === 'undefined' ? 'http://localhost' : window.location.origin);
    // /rest/v1/customers → ['', 'rest', 'v1', 'customers']
    const [, api, , resource] = url.pathname.split('/');
    return {
      api,
      resource,
      filter_keys: [...url.searchParams.keys()].sort(),
    };
  } catch {
    return { api: 'unknown' };
  }
}

/**
 * `fetch` instrumentado para pasarle al cliente de Supabase vía `global.fetch`.
 *
 * Un solo punto cubre TODAS las llamadas: `.from()`, `.rpc()`, `.storage`,
 * `.auth` y `.functions.invoke()`. Es la única forma realista de ver las fallas
 * de red/API completas sin tocar cientos de call sites.
 *
 * A propósito NO se hace monkeypatch de `window.fetch`: interceptaría también
 * los propios envíos de PostHog y se realimentaría.
 */
export function instrumentedFetch(
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<Response> {
  const started = Date.now();
  const raw =
    typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
  const method = init?.method ?? 'GET';

  const report = (props: Record<string, unknown>) => {
    if (!ready()) return;
    try {
      posthogJs.capture('api_request_failed', {
        app: APP_NAME,
        ...describeRequest(raw),
        method,
        duration_ms: Date.now() - started,
        ...props,
      });
    } catch {
      // nunca romper la request por el reporte
    }
  };

  return fetch(input, init).then(
    (res) => {
      if (!res.ok) report({ status: res.status });
      return res;
    },
    (err: unknown) => {
      // Falla de transporte: offline, DNS, CORS, abort.
      if (!isIgnorable(err)) {
        report({ status: 0, network_error: String((err as Error)?.name ?? 'NetworkError') });
      }
      throw err;
    }
  );
}

/**
 * Id de PostHog del visitante actual. Se manda como header `x-ph-distinct-id` a
 * las edge functions para que un Issue del backend apunte a la misma persona
 * que la sesión del frontend.
 */
export function getDistinctId(): string | undefined {
  if (!ready()) return undefined;
  try {
    return posthogJs.get_distinct_id();
  } catch {
    return undefined;
  }
}
